import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { AppState, store } from "../../../redux";
import { history } from "../../../routing/history";
import { OrganizationData } from "../../../models/organizationData";
import { getOrganizationById } from "../../../redux/organization/actions";
import CrudOrganization from "../../../components/core/profile-settings/organization/CrudOrganization";
import CustomTabs from "../../../components/theming/tabs/CustomTabs";
import CompanyHeading from "../../../components/theming/CompanyHeading";
import Loader from "../../../components/theming/loader/Loader";
import routes from "../../../routing/routes";
import InvoiceManagement from "../../../components/core/profile-settings/organization/invoice/InvoiceManagement";
import BookingsManagement from "../../../components/core/profile-settings/organization/bookings/BookingsManagement";
import MembershipTab from "../../../components/core/profile-settings/organization/membership/MembershipTab";
import { useTabPersistence } from "../../../hooks/useTabPersistence";
import OrganizationSettingsManagement from "../../../components/core/profile-settings/organization/settings/OrganizationSettingsManagement";
import { fetchMembershipsByOrganizationId } from "../../../redux/organizationMembership/action";
import { fetchOrganizationSettings } from "../../../redux/organizationSettings/actions";

interface RouteParams {
  id: string;
}

/**
 * Component for managing organization details and memberships.
 * @returns JSX.Element
 */
export const OrganizationAdminPage = () => {
  const { id } = useParams<RouteParams>();
  const { t } = useTranslation(["organization", "snackbars", "invoices"]);
  const dispatch = useDispatch();
  const organizations = useSelector((state: AppState) => state.organizations);
  const user = useSelector((state: AppState) => state.user);
  const [organization, setOrganization] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState(true);

  const isOrganizationInvoiceEnabled =
    store.getState().featureFlagging.featureFlags?.organization_invoice_is_enabled; // TODO ML-2650

  const { activeTab, handleTabChange } = useTabPersistence({ defaultTab: 0 });

  useEffect(() => {
    if (id) {
      dispatch(getOrganizationById(id));
    }
  }, [id, dispatch]);

  // Fetch organization settings when component mounts
  useEffect(() => {
    dispatch(fetchOrganizationSettings(id));
  }, [id]);

  useEffect(() => {
    if (organizations?.organizationData?.length > 0) {
      const selectedOrganization = organizations.organizationData.find(
        (org) => org.id === id
      );
      setOrganization(selectedOrganization || null);
    } else {
      setLoading(false);
    }
  }, [organizations, id]);

  useEffect(() => {
    if (organization && user) {
      const isAdmin = organization.admins.some(
        (admin) => admin?.admin_id === user?.currentUser?.id
      );
      if (isAdmin) {
        setLoading(false);
      } else {
        history.push(routes.home);
      }
    }
  }, [organization, user]);

  const fetchMembershipsData = () => {
    dispatch(fetchMembershipsByOrganizationId(id));
  };

  useEffect(() => {
    if (id) {
      fetchMembershipsData();
    }
  }, [id]);

  const tabs = [
    {
      label: t("bookings.manageBookingsTab", { ns: "organization" }),
      content: <BookingsManagement organizationId={id} />,
    },
    {
      label: t("membership.manageMemberships", { ns: "organization" }),
      content: (
        <MembershipTab organizationId={id} fetchMembershipsData={fetchMembershipsData} />
      ),
    },
    ...(isOrganizationInvoiceEnabled
      ? [
          {
            label: t("invoice.manageInvoices", { ns: "organization" }),
            content: <InvoiceManagement organizationId={id} />,
          },
        ]
      : []),
    {
      label: t("organization", { ns: "organization" }),
      content: organization && <CrudOrganization organizationData={organization} />,
    },
    {
      label: t("orgaSettings", { ns: "organization" }),
      content: <OrganizationSettingsManagement />,
    },
  ];

  return loading ? (
    <Loader />
  ) : !loading && organization ? (
    <>
      <CompanyHeading>{organization?.name}</CompanyHeading>
      <CustomTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
    </>
  ) : (
    <Typography>{t("noOrganization", { ns: "organization" })}</Typography>
  );
};
